import React, { useEffect, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
} from "formik"
import { Grid, Box, styled } from "@mui/material"

import i18n from "config/i18n"
import { useAppSelector, useAppDispatch } from "redux/hooks"
import {
  selectAllAddresses,
  resetRegioOptions,
  selectRegioOptions,
  selectFetchCollectionStatus,
} from "redux/features/geocoding/geocodingSlice"
import { fetchAddresses } from "redux/features/geocoding/geocodingThunk"
import debounce from "helpers/debounce"
import InputAutocomplete from "components/ui/InputAutocomplete"
import InputSelect from "components/ui/InputSelect"
import Input from "components/ui/Input"
import Submit from "components/ui/Submit"
import EmailConfirmationForm from "components/Client/EmailConfirmationForm"
import PhoneConfirmationForm from "components/Client/PhoneConfirmationForm"
import { geocodingCountryOptions } from "helpers/countries"
import { EMAIL_REGEX } from "helpers/email"
import { PHONE_REGEX } from "helpers/client"
import {
  ClientFormValues,
  ClientAddressType,
  FetchStatusType,
  UpdateStatusType,
  SelectOptionType,
  ClientConfirmationFormValues,
} from "types"

interface FormProps {
  initialValues: {
    first_name: string
    last_name: string
    email?: string
    phone?: string
    address?: ClientAddressType
  }
  confirmation: ClientConfirmationFormValues
  fetchStatus: FetchStatusType
  updateStatus: UpdateStatusType
  onConfirmation: () => void
  onSubmit: (values: object) => void
}

type Props = FormikProps<ClientFormValues> & FormProps

const ClientForm = ({
  errors,
  initialValues,
  confirmation,
  setFieldValue,
  setSubmitting,
  fetchStatus,
  updateStatus,
  isSubmitting,
  onConfirmation,
}: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const collection = useAppSelector((state) => selectAllAddresses(state))
  const regioOptions = useAppSelector((state) => selectRegioOptions(state))
  const regioFetchStatus = useAppSelector((state) =>
    selectFetchCollectionStatus(state),
  )

  const { address } = initialValues

  const countryOptions = geocodingCountryOptions()
  const countryOption = address?.country_code
    ? countryOptions.find((c) => c.value == address.country_code)
    : null

  const [currentCountry, setCurrentCountry] = useState(
    countryOption || countryOptions[0],
  )

  const currentRegioOption = address?.id
    ? {
        label: address?.title,
        value: address?.geocoding_id,
      }
    : null

  const { unconfirmed_email, unconfirmed_phone } = confirmation
  const isUnconfirmedEmail = unconfirmed_email && unconfirmed_email.length > 0
  const isUnconfirmedPhone = unconfirmed_phone && unconfirmed_phone.length > 0

  useEffect(() => {
    if (updateStatus && !updateStatus.isProgress && isSubmitting) {
      setSubmitting(false)
    }
  }, [updateStatus])

  useEffect(() => {
    if (regioFetchStatus && !regioFetchStatus.isFetching && address?.title) {
      handleFetchAddresses(address.title)
    }
  }, [])

  const handleChangeCountry = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = countryOptions.find((c) => c.value == event.target.value)

    if (newValue) {
      setCurrentCountry(newValue)
    }
  }

  const handleFetchAddresses = (query: string) => {
    dispatch(fetchAddresses({ country: currentCountry.value, query: query }))
  }

  const handleQueryChange = useCallback(
    debounce((newValue: string) => {
      if (newValue) {
        if (!fetchStatus.isFetching) {
          handleFetchAddresses(newValue)
        }
      } else {
        dispatch(resetRegioOptions())
      }
    }, 300),
    [currentCountry],
  )

  const handleSelect = (newValue: { label: string; value: string }) => {
    if (newValue) {
      const item = collection.find(
        (item: ClientAddressType) => item.geocoding_id === newValue.value,
      )

      if (item && item.complete) {
        setFieldValue("address_attributes", {
          id: address?.id,
          country_code: currentCountry.value,
          ...item,
        })
      } else {
        // TODO: show error message
      }
    } else {
      setFieldValue("address_attributes", {
        id: address?.id,
        _destroy: true,
      })
    }
  }

  return (
    <Form>
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Field name="first_name">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.first_name")}
                  helperText={errors.first_name}
                  disabled
                  {...field}
                />
              )}
            </Field>
          </Grid>
          <Grid item xs={6}>
            <Field name="last_name">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.last_name")}
                  helperText={errors.last_name}
                  disabled
                  {...field}
                />
              )}
            </Field>
          </Grid>

          <Grid item xs={12}>
            <Field name="email">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.email")}
                  helperText={errors.email}
                  disabled={isUnconfirmedEmail || isSubmitting}
                  {...field}
                />
              )}
            </Field>
            <EmailConfirmationForm
              confirmation={confirmation}
              onConfirmation={onConfirmation}
            />
          </Grid>

          <Grid item xs={12}>
            <Field name="phone">
              {({ field }: FieldProps) => (
                <Input
                  fullWidth
                  label={t("features.client.phone")}
                  helperText={errors.phone}
                  disabled={isUnconfirmedPhone || isSubmitting}
                  {...field}
                />
              )}
            </Field>
            <PhoneConfirmationForm
              confirmation={confirmation}
              onConfirmation={onConfirmation}
            />
          </Grid>

          <Grid item xs={3}>
            <Field name="country">
              {() => (
                <InputSelect
                  fullWidth
                  disabled={isSubmitting}
                  defaultValue={currentCountry.value}
                  onChange={handleChangeCountry}
                >
                  {countryOptions.map((c: SelectOptionType) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </InputSelect>
              )}
            </Field>
          </Grid>
          <Grid item xs={9}>
            <Field name="address">
              {() => (
                <InputAutocomplete
                  fullWidth
                  label={t("features.client.address")}
                  defaultValue={currentRegioOption}
                  options={regioOptions}
                  loading={regioFetchStatus.isFetching}
                  disabled={isSubmitting}
                  onInputChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                    newValue: string,
                  ) => handleQueryChange(newValue)}
                  onChange={(
                    event: React.ChangeEvent<HTMLInputElement>,
                    newValue: { label: string; value: string },
                  ) => handleSelect(newValue)}
                />
              )}
            </Field>
          </Grid>
        </Grid>

        <Button type="submit" disabled={isSubmitting}>
          {t("features.client.form.submit")}
        </Button>
      </Container>
    </Form>
  )
}

const ClientFormWithFormik = withFormik<FormProps, ClientFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
  validate: (values: ClientFormValues, form: FormProps) => {
    const errors: FormikErrors<ClientFormValues> = {}
    const { unconfirmed_email } = form.confirmation
    const { unconfirmed_phone } = form.confirmation
    const isUnconfirmedEmail = unconfirmed_email && unconfirmed_email.length > 0
    const isUnconfirmedPhone = unconfirmed_phone && unconfirmed_phone.length > 0

    if (!values.first_name) {
      errors.first_name = i18n.t("validation.field.required", {
        field: i18n.t("features.client.first_name"),
      })
    }

    if (!values.last_name) {
      errors.last_name = i18n.t("validation.field.required", {
        field: i18n.t("features.client.last_name"),
      })
    }

    if (!isUnconfirmedEmail) {
      if (!values.email) {
        errors.email = i18n.t("validation.field.required", {
          field: i18n.t("features.client.email"),
        })
      } else if (!EMAIL_REGEX.test(values.email)) {
        errors.email = i18n.t("validation.field.invalid", {
          field: i18n.t("features.client.email"),
        })
      }
    }

    if (!isUnconfirmedPhone) {
      if (!values.phone) {
        errors.phone = i18n.t("validation.field.required", {
          field: i18n.t("features.client.phone"),
        })
      } else if (!PHONE_REGEX.test(values.phone)) {
        errors.phone = i18n.t("validation.field.invalid", {
          field: i18n.t("features.client.phone"),
        })
      }
    }

    return errors
  },
})(ClientForm)

export default ClientFormWithFormik

export const Container = styled(Box)`
  max-width: 464px;
  margin: 0 auto 23px;
`
export const Button = styled(Submit)`
  border-radius: 21px;
  display: block;
  margin: 23px auto;
  width: 100%;
`
